import { Component, ChangeDetectionStrategy, signal, input, computed, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { AuthenticationService } from '../../../services/authentication.service';
import { ThemeService } from '../../../services/theme.service';

interface NavSubItem {
  label: string;
  route: string;
  icon: string;
}

interface NavItem {
  label: string;
  icon: string;
  route: string;
  requiresWrite?: boolean;
  children?: NavSubItem[];
}

@Component({
  selector: 'app-navbar',
  imports: [RouterLink, RouterLinkActive,NgOptimizedImage, MatIconModule, MatButtonModule, MatMenuModule, MatDividerModule],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent {
  private readonly authService = inject(AuthenticationService);
  private readonly router = inject(Router);
  theme = inject(ThemeService);

  // Inputs
  readonly currentUser = input<any>(null);
  readonly siteTitle = input<string>('HRM (Health & Risk Management)');

  /** True when the current URL starts with /admin, excluding routes that have their own top-level nav item */
  private static readonly isAdminUrl = (url: string) =>
    url.startsWith('/admin') && !url.startsWith('/admin/chatbot');

  protected readonly isAdminRoute = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => NavbarComponent.isAdminUrl(e.urlAfterRedirects)),
    ),
    { initialValue: NavbarComponent.isAdminUrl(this.router.url) },
  );

  // Computed visible nav items based on user authorization
  protected readonly visibleNavItems = computed(() => {
    const user = this.currentUser();
    const allItems: NavItem[] = [
      { label: 'Portal', icon: 'home', route: '/portal', requiresWrite: true },
      { label: 'Data Quality', icon: 'verified', route: '/data-quality', requiresWrite: true },
      { label: 'Transformer Analytics', icon: 'transform', route: '/transformer-analytics', requiresWrite: true },
      { label: 'DAD', icon: 'analytics', route: '/dad', requiresWrite: true },
      { label: 'Knowledge Assistant', icon: 'psychology', route: '/admin/chatbot', requiresWrite: true },
      {
        label: 'Admin', icon: 'admin_panel_settings', route: '/admin', requiresWrite: true,
        children: [
          { label: 'Documentation', route: '/admin/documentation', icon: 'description' },
          { label: 'TechRef', route: '/admin/tech-ref', icon: 'menu_book' },
          { label: 'Asset Annually', route: '/admin/asset-annually', icon: 'fact_check' },
          { label: 'Upload Knowledge Base', route: '/admin/upload-knowledge', icon: 'cloud_upload' },
          { label: 'Cost Optimizer', route: '/cost-optimizer', icon: 'savings' },
        ]
      },
    ];

    if (!user) {
      return [];
    }

    // Filter items based on user authorization
    return allItems.filter((item) => {
      if (item.requiresWrite) {
        return user.authorization?.write;
      }
      return true;
    });
  });

  protected readonly userName = computed(() => {
    return this.currentUser()?.name || 'User';
  });

  /** No photo source is available from MSAL/Entra ID, so we always fall back to initials. */
  protected readonly userInitials = computed(() => {
    const name = this.userName().trim();
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  });

  protected signOut(): void {
    this.authService.signOut();
  }

  protected toggleTheme(): void {
    this.theme.toggle();
  }
}
